import axios from "axios";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { CategoriesData } from "../../../lib/languages";
import { useParams } from "react-router-dom";
import EditorTextArea from "../../../components/EditorTextArea";
import TableSubCategory from "../SubCategory/TableSubCategory";
import DrawerAddSubCategory from "../SubCategory/DrawerAddSubCategory";
import FileUploadImage from "../../../components/FileUploadImage";
import { useAPI } from "../../../context/APIContext";
import LoaderPage from "../../../components/LoaderPage";
import { Spin } from "antd";
import DrawerAddCourse from "../Course/DrawerAddCourse";
import TableCourse from "../Course/TableCourse";

export default function AdminViewCategory() {
  const { id } = useParams();
  const { categoriesItems, subCategoriesItems, coursesItems } = useAPI();
  const { categories, loadingCategories, getAllCategories } = categoriesItems;
  const { loadingSubCategory, subCategories, getAllSubCategories } =
    subCategoriesItems;

  const { loadingCourses, courses, getAllCourses } = coursesItems;
  const coursesOfCategory =
    courses.filter((course) => course?.category?.id === +id) || [];
  // -----------------------------------------

  // Filter categories for the current category
  const oneCategory = categories.filter((cat) => cat.id === +id);
  const subCategoriesOfCategory = subCategories.filter(
    (subCat) => subCat.category.id === +id
  );

  const { language } = useLanguage();
  const t = CategoriesData[language] || CategoriesData["en"];

  const [editorRerender, setEditorRerender] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [image, setImage] = useState(null);
  const [form, setForm] = useState({
    title_en: undefined,
    title_ar: undefined,
    description_en: undefined,
    description_ar: undefined,
    status: false,
  });

  useEffect(() => {
    setEditorRerender(!editorRerender);
    setImage(oneCategory[0]?.image);
    setForm({
      title_en: oneCategory[0]?.title?.en,
      title_ar: oneCategory[0]?.title?.ar,
      description_en: oneCategory[0]?.description?.en,
      description_ar: oneCategory[0]?.description?.ar,
      status: oneCategory[0]?.status,
    });
  }, [categories, id]);

  const handleEditCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }
      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({ ar: form.description_ar, en: form.description_en })
        );
      }
      formData.append("image", image ?? "");
      formData.append("status", form.status ? 1 : 0);

      await axios.post(
        `${BaseUrlApi}/categories/${oneCategory[0]?.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast(t.categoryUpdated, {
        description: t.categoryUpdatedDescription,
      });
      await getAllCategories();
      setEditMode(false);
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (loadingCategories) {
    return <LoaderPage title={t.loadingCategories} />;
  }

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className={"flex items-center justify-between flex-wrap gap-4"}>
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="category">
            {t.editCategory} “
            {oneCategory[0]?.title?.[language] ||
              oneCategory[0]?.title?.en ||
              oneCategory[0]?.title?.ar ||
              "---"}
            ”
          </p>
          <p className="font-medium text-sm">{t.modifyCategory}</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-fit">
          {editMode && (
            <Button
              className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
              onClick={handleEditCategory}
              disabled={loading || !editMode}
            >
              {loading && (
                <div className="animate-spin">
                  <Loader2 />
                </div>
              )}
              {t.editCategory}
            </Button>
          )}
          <Button
            variant={"outline"}
            className={`w-full sm:w-fit h-10 rounded-xl ${
              editMode
                ? "border-2 border-blue-600 text-blue-600 hover:text-blue-500"
                : "bg-blue-600 text-white hover:bg-blue-600/80 hover:text-white"
            }`}
            onClick={() => setEditMode(!editMode)}
            disabled={loading}
          >
            {editMode ? t.showMode : t.editMode}
          </Button>
        </div>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 relative">
        <div className="col-span-1 space-y-4 lg:sticky top-2 self-start">
          {/* Upload Img */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <p className="font-bold text-xl">{t.Thumbnail}</p>
              <FileUploadImage
                onUploadComplete={(url) => setImage(url)}
                image={image}
                name={"categories"}
                disabled={!editMode}
              />
              {errors?.image && (
                <p className="text-red-500 font-semibold text-sm">
                  {errors.image[0]}
                </p>
              )}
              <div className="font-normal text-sm text-muted-foreground text-center">
                {t.imageExtensionMessage}
              </div>
            </CardContent>
          </Card>
          {/* Status */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-4 justify-between border-b pb-2">
                <p className="font-bold text-xl">{t.Status}</p>
                <div
                  className={`size-4 ${
                    !!form?.status ? "bg-green-500" : "bg-red-500"
                  }  rounded-full`}
                />
              </div>
              <div className="grid gap-2">
                <p className="text-sm">{t.selectStatus}</p>
                <Select
                  disabled={!editMode}
                  value={!!form?.status}
                  onValueChange={(e) => setForm({ ...form, status: e })}
                >
                  <SelectTrigger
                    className="rounded-lg sm:ml-auto"
                    aria-label="Select status"
                  >
                    <SelectValue placeholder={t.selectStatus} />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value={true} className="rounded-lg">
                      {t.active}
                    </SelectItem>
                    <SelectItem value={false} className="rounded-lg">
                      {t.inactive}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="col-span-1 lg:col-span-2">
          {/* General information */}
          <Card className="h-full flex flex-col">
            <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
              <p className="font-bold text-xl border-b pb-2">
                {t.generalInformation}
              </p>
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_en" className="text-sm">
                      {t.titleEn}
                    </label>
                    <Input
                      readOnly={!editMode}
                      id="title_en"
                      name="title_en"
                      type="text"
                      placeholder={t.enterTitleEn}
                      value={form.title_en}
                      onChange={(e) =>
                        setForm({ ...form, title_en: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <label htmlFor="title_ar" className="text-sm">
                      {t.titleAr}
                    </label>
                    <Input
                      readOnly={!editMode}
                      id="title_ar"
                      name="title_ar"
                      type="text"
                      placeholder={t.enterTitleAr}
                      value={form.title_ar}
                      onChange={(e) =>
                        setForm({ ...form, title_ar: e.target.value })
                      }
                    />
                    {errors?.title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.title[0]}
                      </p>
                    )}
                  </div>
                </div>
                {/* Description */}
                <div className="flex flex-col gap-2 flex-1">
                  <label htmlFor="description" className="text-sm">
                    {t.descriptionAr} & {t.descriptionEn}
                  </label>
                  <EditorTextArea
                    EditorRerender={editorRerender}
                    form={form}
                    setForm={setForm}
                    disabled={!editMode}
                    en={"description_en"}
                    ar={"description_ar"}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sub Categories */}
      <div className="">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">{t.subCategories}</h1>
          <DrawerAddSubCategory
            getAllSubCategories={getAllSubCategories}
            item={oneCategory[0]}
          />
        </div>
        {!loadingSubCategory ? (
          <TableSubCategory
            data={subCategoriesOfCategory}
            getAllSubCategories={getAllSubCategories}
            category={oneCategory[0]}
          />
        ) : (
          <div className="h-40 w-full flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}
      </div>

      {/* Courses */}
      <div className="">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">{t.courses}</h1>
          <DrawerAddCourse
            getAllCourses={getAllCourses}
            item={oneCategory[0]}
            type={"category"}
          />
        </div>

        {!loadingCourses ? (
          <TableCourse
            data={coursesOfCategory}
            getAllCourses={getAllCourses}
            subCategory={oneCategory[0]}
          />
        ) : (
          <div className="h-40 w-full flex items-center justify-center">
            <Spin size="large" />
          </div>
        )}
      </div>
    </div>
  );
}
